import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import DropDown from "../DropDown";
import { DropDownItem } from "../../atoms/DropDownItem";
import classNames from "../../../utils/classNames";
import "../PanelInputField/index.scss";
import "./index.scss";

const defaultGetOptionValue = (option) => option?.value ?? "";
const defaultGetOptionLabel = (option) => option?.label ?? "";

export default function PanelDropdownField({
    label,
    id,
    value = "",
    options,
    onChange,
    disabled = false,
    className = "",
    fieldRef,
    fieldProps = {},
    triggerClassName = "",
    menuClassName = "",
    itemClassName = "",
    valueClassName = "",
    caretClassName = "",
    placeholder = "",
    getOptionValue = defaultGetOptionValue,
    getOptionLabel = defaultGetOptionLabel,
    noItemsMessage = "No items found.",
}) {
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const labelId = `${id}-label`;
    const valueId = `${id}-value`;
    const selectedOption = useMemo(
        () => options.find((option) => getOptionValue(option) === value) || null,
        [getOptionValue, options, value],
    );

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            const clickedInsideField = containerRef.current?.contains(event.target);
            const clickedInsideMenu = dropdownRef.current?.contains(event.target);

            if (!clickedInsideField && !clickedInsideMenu) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (disabled && isOpen) {
            setIsOpen(false);
        }
    }, [disabled, isOpen]);

    const handleTriggerKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
        }
    };

    const handleSelect = (option) => {
        const nextValue = getOptionValue(option);
        onChange(nextValue, option);
        setIsOpen(false);

        const focusTrigger = () => {
            triggerRef.current?.focus();
        };

        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(focusTrigger);
            return;
        }

        focusTrigger();
    };

    const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : placeholder;

    return (
        <div
            className={classNames("panel-input-field", "panel-dropdown-field", className)}
            ref={(node) => {
                containerRef.current = node;
                if (!fieldRef) {
                    return;
                }
                if (typeof fieldRef === "function") {
                    fieldRef(node);
                    return;
                }
                fieldRef.current = node;
            }}
            {...fieldProps}
        >
            <label id={labelId} htmlFor={id} className="panel-input-field__label">{label}</label>
            <button
                id={id}
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-labelledby={`${labelId} ${valueId}`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className={classNames(
                    "panel-input-field__input",
                    "panel-dropdown-field__trigger",
                    { "is-open": isOpen },
                    triggerClassName,
                )}
                onClick={() => setIsOpen((previous) => !previous)}
                onKeyDown={handleTriggerKeyDown}
            >
                <span
                    id={valueId}
                    className={classNames("panel-dropdown-field__value", valueClassName)}
                >
                    {selectedLabel}
                </span>
                <span
                    className={classNames(
                        "panel-dropdown-field__caret",
                        { "is-open": isOpen },
                        caretClassName,
                    )}
                    aria-hidden="true"
                >
                    ▾
                </span>
            </button>
            {isOpen && (
                <DropDown
                    dropdownRef={dropdownRef}
                    anchorRef={triggerRef}
                    items={options}
                    className={classNames("panel-dropdown-field__menu", menuClassName)}
                    menuRole="listbox"
                    noItemsMessage={noItemsMessage}
                    placement="bottom-start"
                    strategy="fixed"
                    matchTriggerWidth
                    renderInPortal
                    getItemId={(option) => getOptionValue(option)}
                    renderItem={(option) => {
                        const optionValue = getOptionValue(option);
                        const optionLabel = getOptionLabel(option);
                        const isSelected = optionValue === value;

                        return (
                            <DropDownItem
                                key={optionValue}
                                className={classNames(
                                    "panel-dropdown-field__item",
                                    { "is-selected": isSelected },
                                    itemClassName,
                                )}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleSelect(option)}
                            >
                                {optionLabel}
                            </DropDownItem>
                        );
                    }}
                />
            )}
        </div>
    );
}

PanelDropdownField.propTypes = {
    label: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    fieldRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
    fieldProps: PropTypes.object,
    triggerClassName: PropTypes.string,
    menuClassName: PropTypes.string,
    itemClassName: PropTypes.string,
    valueClassName: PropTypes.string,
    caretClassName: PropTypes.string,
    placeholder: PropTypes.string,
    getOptionValue: PropTypes.func,
    getOptionLabel: PropTypes.func,
    noItemsMessage: PropTypes.string,
};
